import { memo } from 'react';
import { cn, Discount, Price } from '@divlab/divanui';

import useTranslation from '@Queries/useTranslation';
import { useRequest } from '@Contexts/Request';
import ProductLike from '@Components/ProductLike';
import InfoTab from '@Components/InfoTab';
import getProductName from '@Utils/getProductName';
import Preview from '../Preview';
import Button from './elements/Button';
import styles from './ProductCard.module.css';

import type { HTMLAttributes, FC } from 'react';
import type { ProductData } from '@Types/Product';

export interface ProductCardProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  product: ProductData;
}

const ProductCard: FC<ProductCardProps> = (props) => {
  const { product, ...restProps } = props;
  const hasExpired = product.price.expired > 0;
  const isComingSoon = product.status === 'ComingSoon';
  const { country } = useRequest();
  const { t } = useTranslation();
  const isUZB = country === 'UZB';

  return (
    <div {...restProps}>
      <div>
        <div className={styles.previewWrapper}>
          <Preview
            product={product}
            className={cn(styles.preview, { [styles.notAvailable]: !product.isActive })}
          />
          <ProductLike className={styles.trashIcon} product={product} />
        </div>
        <div className={styles.containerInfo}>
          {product.isActive && (
            <div>
              <div className={styles.name}>{getProductName(product)}</div>
              <div className={styles.price}>
                <Price className={styles.actualPrice} price={product.price.actual} />
                {hasExpired && (
                  <Price
                    expired
                    withoutCurrency={isUZB}
                    className={cn(styles.expiredPrice, { [styles.longPrice]: isUZB })}
                    price={product.price.expired}
                  />
                )}
                {product.price.discount > 0 && !isUZB && (
                  <Discount
                    className={styles.discount}
                    value={product.price.discount}
                    view='grayStroke'
                  />
                )}
              </div>
              {product.isConstructor && (
                <span className={styles.tile}>{t('individual_execution')}</span>
              )}
            </div>
          )}
          <InfoTab
            showIcon={isComingSoon || !product.isActive}
            unavailable={!product.isActive}
            comingSoon={isComingSoon}
          />
        </div>
      </div>
      <Button product={product} />
    </div>
  );
};

export default memo(ProductCard);
